import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSearchParamsUpdater } from 'hooks/useSearchParamsUpdater';
import SessionStorageUtils from 'utils/SessionStorageUtils';
import { getAvailableLocations } from 'helpers/getAvailableLocations';
import { SessionStorageKeys } from 'constants/sessionStorageKeys';
import { Dropdown } from 'components/Dropdown';

export const ChooseLocation = ({ className }) => {
  const { locations: allLocations } = useSelector((state) => state.locations);
  const { user } = useSelector((state) => state.auth);
  const query = useSearchParamsUpdater();
  const { locationName } = useParams();

  const locationTransliteration = query.getParam('location') || locationName;

  const locations = useMemo(() => getAvailableLocations(user, allLocations), [user, allLocations]);

  const getLocationByTransliteration = useCallback(
    (transliteration) => locations.find((l) => l.transliteration === transliteration),
    [locations]
  );

  useEffect(() => {
    if (locations.length === 0 || locationTransliteration) return;

    const saved = SessionStorageUtils.getData(SessionStorageKeys.SCHEDULE_LOCATION);
    const fallback = saved || locations[0];

    query.setParam('location', fallback.transliteration);
  }, [locations]);

  const shouldShowSelector = useMemo(() => {
    const hasMultiple = user?.locations?.length > 1;
    const isClubManager = user?.role === 3;
    return !locationName && locations.length > 0 && user && (isClubManager || hasMultiple);
  }, [user, locations, locationName]);

  const locationOptions = useMemo(
    () => locations.map(({ transliteration, name }) => ({ label: name, value: transliteration })),
    [locations]
  );

  const selectedOption = useMemo(() => {
    const location = getLocationByTransliteration(locationTransliteration);
    return location ? { label: location.name, value: location.transliteration } : null;
  }, [locationTransliteration, getLocationByTransliteration]);

  const handleChange = useCallback(
    (option) => {
      const full = getLocationByTransliteration(option.value);
      if (!full) return;
      query.setParam('location', full.transliteration);
      SessionStorageUtils.setData(SessionStorageKeys.SCHEDULE_LOCATION, full);
    },
    [getLocationByTransliteration, query]
  );

  if (!shouldShowSelector) return null;

  return (
    <Dropdown
      className={className}
      options={locationOptions}
      value={selectedOption}
      onChange={handleChange}
      clearable={false}
    />
  );
};
